<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-location-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-6 py-4"
      >
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-map-marker</v-icon>
          Edit Location
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Update your agency's physical location and address details
        </div>
      </v-card-text>

      <!-- Map Preview -->
      <v-card-text class="px-6 py-4">
        <div
          class="map-preview rounded-lg elevation-2"
          @click="openMapFullscreen"
        >
          <div
            class="map-placeholder d-flex flex-column align-center justify-center"
          >
            <v-icon x-large color="primary" class="mb-2">mdi-map</v-icon>
            <div class="text-caption">Click to set location on map</div>
          </div>
          <div v-if="mapLocationSet" class="map-marker">
            <v-icon color="error" large>mdi-map-marker</v-icon>
          </div>
        </div>
      </v-card-text>

      <!-- Error/Success message -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="
              messageType === 'success'
                ? 'mdi-check-circle'
                : 'mdi-alert-circle'
            "
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Address Form -->
      <v-card-text class="px-6 py-0">
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="profile.address"
              label="Street Address*"
              outlined
              dense
              prepend-inner-icon="mdi-home-map-marker"
              :rules="[rules.required, rules.address]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="profile.address2"
              label="Apt/Suite (Optional)"
              outlined
              dense
              prepend-inner-icon="mdi-home-city"
              @input="onChange"
              class="modern-input"
            />
          </v-col>
          <v-col cols="12" sm="6" class="py-0">
            <v-text-field
              v-model="profile.city"
              label="City*"
              outlined
              dense
              prepend-inner-icon="mdi-city"
              :rules="[rules.required, rules.city]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>
          <v-col cols="12" sm="3" class="py-0">
            <v-select
              v-model="profile.state"
              label="State*"
              outlined
              dense
              :items="stateOptions"
              prepend-inner-icon="mdi-earth"
              :rules="[rules.required, rules.state]"
              @change="onChange"
              class="modern-input"
            />
          </v-col>
          <v-col cols="12" sm="3" class="py-0">
            <v-text-field
              v-model="profile.zip"
              label="ZIP Code*"
              outlined
              dense
              prepend-inner-icon="mdi-barcode"
              :rules="[rules.required, rules.zip]"
              @input="formatZipCode"
              class="modern-input"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          width="160"
          :loading="loading"
          :disabled="!formValid"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save Location
        </v-btn>
      </v-card-actions>

      <!-- Fullscreen Map Dialog -->
      <v-dialog
        v-model="mapDialog"
        fullscreen
        transition="dialog-bottom-transition"
      >
        <v-card>
          <v-toolbar dark color="primary">
            <v-btn icon dark @click="mapDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title>Set Your Location</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn dark text @click="confirmMapLocation">
                Confirm Location
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <div class="fullscreen-map">
            <!-- In a real implementation, you would integrate with Google Maps or Mapbox here -->
            <div
              class="map-container-placeholder d-flex align-center justify-center"
            >
              <div class="text-center">
                <v-icon x-large class="mb-4" color="primary">mdi-map</v-icon>
                <h3 class="mb-2">Map Integration</h3>
                <p class="body-1">This would display an interactive map</p>
                <p class="caption">
                  (Google Maps/Mapbox would be implemented here)
                </p>
              </div>
            </div>
          </div>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      messageType: "error",
      loading: false,
      mapDialog: false,
      mapLocationSet: false,
      rules: {
        required: value => !!value || "This field is required",
        address: value =>
          value.trim().length > 5 || "Address must be at least 6 characters",
        city: value =>
          /^[a-zA-Z\s]+$/.test(value) || "City must contain only letters",
        state: value =>
          this.stateOptions.includes(value) || "Invalid state selected",
        zip: value =>
          /^\d{5}(-\d{4})?$/.test(value) || "Invalid ZIP code format"
      },
      profile: {},
      stateOptions: [
        "AL",
        "AK",
        "AZ",
        "AR",
        "CA",
        "CO",
        "CT",
        "DE",
        "FL",
        "GA",
        "HI",
        "ID",
        "IL",
        "IN",
        "IA",
        "KS",
        "KY",
        "LA",
        "ME",
        "MD",
        "MA",
        "MI",
        "MN",
        "MS",
        "MO",
        "MT",
        "NE",
        "NV",
        "NH",
        "NJ",
        "NM",
        "NY",
        "NC",
        "ND",
        "OH",
        "OK",
        "OR",
        "PA",
        "RI",
        "SC",
        "SD",
        "TN",
        "TX",
        "UT",
        "VT",
        "VA",
        "WA",
        "WV",
        "WI",
        "WY",
        "AS",
        "DC",
        "FM",
        "GU",
        "MH",
        "MP",
        "PW",
        "PR",
        "VI"
      ]
    };
  },
  computed: {
    ...mapState("auth", {
      myProfile: "profile"
    }),
    formValid() {
      return (
        this.profile.address &&
        this.profile.city &&
        this.profile.state &&
        this.profile.zip &&
        this.rules.address(this.profile.address) === true &&
        this.rules.city(this.profile.city) === true &&
        this.rules.state(this.profile.state) === true &&
        this.rules.zip(this.profile.zip) === true
      );
    }
  },
  methods: {
    ...mapActions("auth", {
      updateProfile: "updateProfile"
    }),
    onChange() {
      this.message = null;
    },
    // formatZipCode() {
    //   // Auto-format ZIP code to #####-#### pattern
    //   if (
    //     this.profile.zip &&
    //     this.profile.zip.length === 5 &&
    //     /^\d+$/.test(this.profile.zip)
    //   ) {
    //     this.profile.zip = this.profile.zip + "";
    //   }
    // },
      formatZipCode() {
    // Only allow digits, and optionally a single dash for ZIP+4
    let val = this.profile.zip || '';
    // Remove all non-digit and non-dash characters
    val = val.replace(/[^0-9-]/g, '');
    // Only allow one dash, and only after 5 digits
    if (val.includes('-')) {
      const parts = val.split('-');
      val = parts[0].slice(0, 5);
      if (parts[1]) {
        val += '-' + parts[1].slice(0, 4);
      }
    } else {
      val = val.slice(0, 9); // max 9 digits if no dash
    }
    this.profile.zip = val;
  },
    openMapFullscreen() {
      this.mapDialog = true;
    },
    confirmMapLocation() {
      // In a real implementation, this would set the geocoordinates
      this.mapLocationSet = true;
      this.mapDialog = false;
    },
    async onSave() {
      if (!this.formValid) {
        this.message = "Please complete all required fields correctly";
        this.messageType = "error";
        return;
      }

      this.loading = true;
      try {
        const locationData = {
          address: this.profile.address,
          address2: this.profile.address2,
          city: this.profile.city,
          state: this.profile.state,
          zip: this.profile.zip
          // In a real implementation, you'd include:
          // latitude: this.latitude,
          // longitude: this.longitude
        };

        await this.updateProfile(locationData);
        this.message = "Location updated successfully!";
        this.messageType = "success";
        setTimeout(() => {
          this.onClose();
        }, 1500);
      } catch (error) {
        this.message =
          error.response?.data?.message || "An error occurred while saving";
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    myProfile(newValue) {
      this.profile = { ...newValue };
    }
  },
  mounted() {
    this.profile = { ...this.myProfile };
    this.message = null;
  }
};
</script>

<style scoped>
.modern-location-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
    background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.map-preview {
  height: 180px;
  background-color: #f5f5f5;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 16px;
}

.map-preview:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.map-placeholder {
  height: 100%;
  width: 100%;
  background-color: #f9f9f9;
  color: #666;
}

.map-marker {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -100%);
}

.fullscreen-map {
  height: calc(100vh - 64px);
  width: 100%;
}

.map-container-placeholder {
  height: 100%;
  width: 100%;
  background-color: #e9f5ff;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  /* border: 1px solid #e0e0e0; */
  background-color: white !important;
}

/* .modern-input::v-deep .v-input__slot:hover {
  border-color: var(--v-primary-base);
} */

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-location-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-location-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-location-dialog::-webkit-scrollbar-thumb {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-location-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}
</style>
